/**
 * referenceUrl — AP3. URL-display helpers for the appendix:
 *
 *   - `hostnameOf(url)`        — bare hostname (`www.` stripped) for
 *                                hyperlink visible text.
 *   - `adamsUrlForAccession()` — synthesizes the public ADAMS URL for
 *                                a bare accession number when the
 *                                reference row didn't carry one.
 *   - `formatIsoDate()`        — ISO `YYYY-MM-DD` from a Date / ISO
 *                                string. AP6 reuses this; co-located
 *                                here because the appendix URL render
 *                                already needs a date next to it.
 *
 * Pure functions — no React deps so the helpers are equally callable
 * from the DOCX exporter.
 */

/** Strip the scheme + leading `www.`; on parse failure return the raw input. */
export function hostnameOf(url: string): string {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./i, '');
  } catch {
    return url;
  }
}

/** Detect a bare NRC ADAMS accession number (e.g. ML003702019). */
export function isAdamsAccession(token: unknown): boolean {
  // Older saved/public references may omit their optional display category.
  // An absent category is not an accession; preserve the source and URL.
  return typeof token === 'string' && /^ML\d{6,}$/i.test(token.trim());
}

/** Build the canonical ADAMS public-search URL for a bare accession. */
export function adamsUrlForAccession(accession: string): string {
  return `https://adams.nrc.gov/wba/view?accessionNumber=${accession.trim()}`;
}

/**
 * Render an ISO date string (YYYY-MM-DD) from a Date or ISO string.
 * Drops time-of-day. Returns `'unknown'` on parse failure so the
 * appendix never silently renders an empty cell.
 */
export function formatIsoDate(input: string | Date | undefined | null): string {
  if (!input) return 'unknown';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return 'unknown';
  // toISOString returns UTC — date-only slice is timezone-stable for
  // appendix display (no per-locale drift).
  return d.toISOString().slice(0, 10);
}
